/**
 * Live schedule-registry broadcasts — a schedule fired, folded, edited or
 * deleted — delivered through the shared `global_events` room (PRD-0359 D-16).
 * A report's delivery progress is not among them: the integrations channel
 * announces it with its own delivery frame (PRD-0401 D-13).
 *
 * `disp_schedule_changed` lands only in that room, whose one entry point is a
 * client-emitted `subscribe_global`. A bare handler attach never sends the
 * join, so a list subscribed that way hears the frame only when some other
 * page has left the socket in the room; this domain owns the join and replays
 * it after every reconnect.
 */

import type { DispatchScheduleChangedBroadcast } from '@/types/dispatch/schedules'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

const SCHEDULE_CHANGED_EVENT = 'disp_schedule_changed'

export class DispatchScheduleLiveApi {
  private readonly events: SubscriptionRegistry<null, DispatchScheduleChangedBroadcast>

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Idempotent server-side, and never left: other domains share the room.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /** Subscribe to every schedule frame; the callback receives its payload. */
  subscribeToDispatchScheduleEvents(
    callback: (payload: DispatchScheduleChangedBroadcast) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    this.client.attachOnce(SCHEDULE_CHANGED_EVENT, (raw) => {
      // Identity-only and never rendered: consumers refetch on any frame.
      this.events.dispatch(null, raw as DispatchScheduleChangedBroadcast)
    })

    return this.events.subscribe(null, callback)
  }

  /** Re-join global_events for every live listener after a reconnect. */
  replay(): void {
    this.events.replay()
  }

  clear(): void {
    this.events.clear()
  }
}
